import { useState, useEffect, Fragment } from 'react';

import CheckIcon from '@common/assets/icons/check.svg?react';
import PickerIcon from '@common/assets/icons/picker.svg?react';
import { Card } from '@common/ui/Card';
import { Cell } from '@common/ui/Cell';
import { Modal } from '@common/ui/Modal';
import { Separator } from '@common/ui/Separator';

import styles from './InputSelect.module.scss';
import { Input, type InputProps } from '../Input';

export type InputSelectItem = {
  value: string;
  title: string;
  subtitle?: string;
};

export interface InputSelectProps
  extends Omit<InputProps, 'onSelect' | 'onChange'> {
  value?: string;
  items?: InputSelectItem[];
  onSelect?: (value: string) => void;
  gap?: number;
  isCloseAfterSelect?: boolean;
  isOpenModal: boolean;
  setIsOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
}

export const InputSelect = ({
  items,
  onSelect,
  gap,
  isCloseAfterSelect = true,
  isOpenModal,
  setIsOpenModal,
  value,
  ...props
}: InputSelectProps) => {
  const [selected, setSelected] = useState<string>();

  useEffect(() => {
    if (value) {
      setSelected(value);
    } else {
      setSelected(undefined);
    }
  }, [value]);

  useEffect(() => {
    if (!isOpenModal && selected && selected !== value) {
      onSelect?.(selected);
    }
  }, [selected, isOpenModal]);

  const onOpenHandler = () => {
    setIsOpenModal(true);
  };

  const onCloseHandler = () => {
    setIsOpenModal(false);
    props.onBlur?.({} as React.FocusEvent<HTMLInputElement>);
  };

  const onSelectHandler = (value: string) => {
    setSelected(value);
    onSelect?.(value);
    isCloseAfterSelect && onCloseHandler();
  };

  return (
    <div className={styles.inputSelect} data-test-id="input-select-container">
      <div
        onClick={onOpenHandler}
        style={{ pointerEvents: 'auto' }}
        data-test-id="input-select-trigger"
      >
        <Input
          {...props}
          value={
            items?.find((item: InputSelectItem) => selected === item.value)
              ?.title
          }
          data-test-id="input-select-input"
          suffixIcon={<PickerIcon />}
          onClick={onOpenHandler}
          style={{ pointerEvents: 'none' }}
        />
      </div>
      {
        <Modal
          testId="input-select-modal"
          className={styles.modal}
          open={isOpenModal}
          onClose={onCloseHandler}
        >
          <Card
            testId="input-select-options"
            style={{ gridGap: gap, padding: '4px 16px' }}
          >
            {items?.map((item, index) => (
              <Fragment key={item.value}>
                <Cell
                  testId={`input-select-option-${item.value}`}
                  title={item.title}
                  subtitle={item.subtitle}
                  suffixIcon={
                    item.value === selected && (
                      <CheckIcon className={styles.icon} />
                    )
                  }
                  onClick={() => onSelectHandler(item.value)}
                />
                {index < items.length - 1 && <Separator />}
              </Fragment>
            ))}
          </Card>
        </Modal>
      }
    </div>
  );
};
